import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  fetchOfficers, fetchSites, fetchLateReports, fetchShiftPasswords, fetchCheckpoints,
  fetchCheckIns, fetchIncidents, fetchSchedules,
  createOfficer, updateOfficer, deleteOfficer,
  createSite, deleteSite, updateSite,
  createCheckpoint, deleteCheckpoint,
  createSchedule, deleteSchedule, updateSchedule,
  createIncident, updateIncident,
  issueShiftPassword, issueAllPasswords,
  generateRotationSchedules,
  subscribeToTable,
  type DbOfficer, type DbSite, type DbLateReport, type DbShiftPassword,
} from "@/lib/supabaseQueries";
import type { Database } from "@/integrations/supabase/types";
import { toast } from "sonner";

export function useBusinessData() {
  const queryClient = useQueryClient();
  const { user, loading } = useAuth();
  const isReady = !loading && !!user;

  // ========== QUERIES (gated on auth readiness) ==========
  const officersQuery = useQuery({
    queryKey: ["officers"],
    queryFn: fetchOfficers,
    enabled: isReady,
    retry: 2,
    refetchInterval: 10000, // Poll every 10s as realtime fallback for status sync
  });

  const sitesQuery = useQuery({
    queryKey: ["sites"],
    queryFn: fetchSites,
    enabled: isReady,
    retry: 2,
  });

  const lateReportsQuery = useQuery({
    queryKey: ["late_reports"],
    queryFn: fetchLateReports,
    enabled: isReady,
    retry: 2,
  });

  const shiftPasswordsQuery = useQuery({
    queryKey: ["shift_passwords"],
    queryFn: fetchShiftPasswords,
    enabled: isReady,
    retry: 2,
  });

  const checkpointsQuery = useQuery({
    queryKey: ["checkpoints"],
    queryFn: fetchCheckpoints,
    enabled: isReady,
    retry: 2,
  });

  const checkInsQuery = useQuery({
    queryKey: ["checkIns"],
    queryFn: fetchCheckIns,
    enabled: isReady,
    retry: 2,
    refetchInterval: 15000, // Poll every 15s for fresh check-in data
  });

  const incidentsQuery = useQuery({
    queryKey: ["incidents"],
    queryFn: fetchIncidents,
    enabled: isReady,
    retry: 2,
    refetchInterval: 15000, // Poll every 15s for fresh incident data
  });

  const schedulesQuery = useQuery({
    queryKey: ["schedules"],
    queryFn: fetchSchedules,
    enabled: isReady,
    retry: 2,
    refetchInterval: 15000, // Poll every 15s for fresh schedule data
  });

  // ========== REAL-TIME SUBSCRIPTIONS ==========
  useEffect(() => {
    const unsubs = [
      subscribeToTable("officers", () => queryClient.invalidateQueries({ queryKey: ["officers"] })),
      subscribeToTable("sites", () => queryClient.invalidateQueries({ queryKey: ["sites"] })),
      subscribeToTable("late_reports", () => queryClient.invalidateQueries({ queryKey: ["late_reports"] })),
      subscribeToTable("shift_passwords", () => queryClient.invalidateQueries({ queryKey: ["shift_passwords"] })),
      subscribeToTable("check_ins", () => queryClient.invalidateQueries({ queryKey: ["checkIns"] })),
      subscribeToTable("incidents", () => queryClient.invalidateQueries({ queryKey: ["incidents"] })),
      subscribeToTable("checkpoints", () => queryClient.invalidateQueries({ queryKey: ["checkpoints"] })),
    ];
    return () => unsubs.forEach(fn => fn());
  }, [queryClient]);

  // ========== MUTATIONS ==========
  const addOfficerMutation = useMutation({
    mutationFn: (officer: Database["public"]["Tables"]["officers"]["Insert"]) => createOfficer(officer),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["officers"] });
    },
  });

  const updateOfficerMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Database["public"]["Tables"]["officers"]["Update"] }) =>
      updateOfficer(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["officers"] });
    },
  });

  const removeOfficerMutation = useMutation({
    mutationFn: (id: string) => deleteOfficer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["officers"] });
    },
  });

  const addSiteMutation = useMutation({
    mutationFn: (site: Database["public"]["Tables"]["sites"]["Insert"]) => createSite(site),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sites"] });
    },
  });

  const updateSiteMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Database["public"]["Tables"]["sites"]["Update"] }) =>
      updateSite(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sites"] });
    },
  });

  const removeSiteMutation = useMutation({
    mutationFn: (id: string) => deleteSite(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sites"] });
    },
  });

  const addCheckpointMutation = useMutation({
    mutationFn: (checkpoint: Database["public"]["Tables"]["checkpoints"]["Insert"]) => createCheckpoint(checkpoint),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["checkpoints"] });
    },
  });

  const removeCheckpointMutation = useMutation({
    mutationFn: (id: string) => deleteCheckpoint(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["checkpoints"] });
    },
  });

  const issuePasswordMutation = useMutation({
    mutationFn: ({ officerId, expiryHours }: { officerId: string; expiryHours: number }) =>
      issueShiftPassword(officerId, expiryHours),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shift_passwords"] });
    },
  });

  const issueAllPasswordsMutation = useMutation({
    mutationFn: ({ officerIds, expiryHours }: { officerIds: string[]; expiryHours: number }) =>
      issueAllPasswords(officerIds, expiryHours),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shift_passwords"] });
    },
  });

  const addScheduleMutation = useMutation({
    mutationFn: (schedule: Database["public"]["Tables"]["schedules"]["Insert"]) => createSchedule(schedule),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedules"] });
    },
  });

  const removeScheduleMutation = useMutation({
    mutationFn: (id: string) => deleteSchedule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedules"] });
    },
  });

  const addIncidentMutation = useMutation({
    mutationFn: (incident: Database["public"]["Tables"]["incidents"]["Insert"]) => createIncident(incident),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
    },
  });

  const updateIncidentMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Database["public"]["Tables"]["incidents"]["Update"] }) =>
      updateIncident(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
    },
  });

  const updateScheduleMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Database["public"]["Tables"]["schedules"]["Update"] }) =>
      updateSchedule(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedules"] });
    },
  });

  const generateRotationMutation = useMutation({
    mutationFn: (params: { siteId: string; officerIds: string[]; checkpointIds: string[]; date: string; shiftStart: string; shiftEnd: string; intervalMinutes: number }) =>
      generateRotationSchedules(params.siteId, params.officerIds, params.checkpointIds, params.date, params.shiftStart, params.shiftEnd, params.intervalMinutes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedules"] });
    },
  });

  return {
    // Data
    officers: officersQuery.data ?? [],
    sites: sitesQuery.data ?? [],
    lateReports: lateReportsQuery.data ?? [],
    shiftPasswords: shiftPasswordsQuery.data ?? [],
    checkpoints: checkpointsQuery.data ?? [],
    checkIns: checkInsQuery.data ?? [],
    incidents: incidentsQuery.data ?? [],
    schedules: schedulesQuery.data ?? [],
    isLoading: officersQuery.isLoading || sitesQuery.isLoading,

    // Mutations
    addOfficer: addOfficerMutation,
    updateOfficer: updateOfficerMutation,
    removeOfficer: removeOfficerMutation,
    addSite: addSiteMutation,
    updateSite: updateSiteMutation,
    removeSite: removeSiteMutation,
    addCheckpoint: addCheckpointMutation,
    removeCheckpoint: removeCheckpointMutation,
    issuePassword: issuePasswordMutation,
    issueAllPasswords: issueAllPasswordsMutation,
    addSchedule: addScheduleMutation,
    updateSchedule: updateScheduleMutation,
    removeSchedule: removeScheduleMutation,
    addIncident: addIncidentMutation,
    updateIncident: updateIncidentMutation,
    generateRotation: generateRotationMutation,
  };
}
